
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  DialogContent,
  DialogContentText,
  Grid,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import Popup from "../../../components/common/model/Popup";
import useStyles from "../../../components/common/model/styles";
import FormField from "../../../components/common/FormField";
import Notification from "../../../components/common/notification/Notification";
import ResetFormAlert from "./ResetFormAlert";

import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";

import { roleAllFields } from "./userAllFields";

import {
  addRoleAction,
  getAllModules,
  setAddRoleResponse,
} from "../../../store/reducers/roleSlice";

import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import Validation from "../../../utils/Validation";

// --------------------------------------------------
// Types
// --------------------------------------------------

interface ManageRoleProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectRole: any;
}

interface Module {
  module_id: number;
  module_name: string;
  panel: string;
  created_at: string;
}

type PermissionType = "create" | "read" | "update" | "delete";

interface ModulePermission {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

interface RoleDetail {
  role: string;
  role_id?: string;
  description: string;
  permissions: Record<string, ModulePermission>;
  [key: string]: any;
}

const permissionTypes: PermissionType[] = [
  "create",
  "read",
  "update",
  "delete",
];

// --------------------------------------------------
// Module helpers
// --------------------------------------------------

// Normalize module names for matching.
// Example:
// "Setting: Customer" -> "setting:customer"
// "Setting:Customer"  -> "setting:customer"

const normalizeModuleName = (name: string): string => {
  return name
    .trim()
    .replace(/\s*:\s*/g, ":")
    .toLowerCase();
};

// Create empty permissions dynamically from API modules.

const createRolePermissions = (
  modules: Module[] = [],
): Record<string, ModulePermission> => {
  return modules
    .filter((module) => module.panel === "User Panel")
    .reduce((acc, module) => {
      const moduleName = module.module_name.trim();

      const existingModule = Object.keys(acc).find(
        (key) => normalizeModuleName(key) === normalizeModuleName(moduleName),
      );

      // Avoid duplicate module names.
      if (!existingModule) {
        acc[moduleName] = {
          create: false,
          read: false,
          update: false,
          delete: false,
        };
      }

      return acc;
    }, {} as Record<string, ModulePermission>);
};

// Generate permissions for Add/Edit Role.

const buildRoleDetail = (
  modules: Module[],
  selectRole?: any,
): RoleDetail => {
  const permissions = createRolePermissions(modules);

  if (selectRole?.modules?.length) {
    selectRole.modules.forEach((module: any) => {
      const moduleName = normalizeModuleName(module.module_name);

      const matchedKey = Object.keys(permissions).find(
        (key) => normalizeModuleName(key) === moduleName,
      );

      if (!matchedKey) return;

      module.permissions?.forEach((permission: string) => {
        if (permissionTypes.includes(permission as PermissionType)) {
          permissions[matchedKey][permission as PermissionType] = true;
        }
      });
    });
  }

  return {
    role: selectRole?.role || "",
    role_id: selectRole?.role_id || "",
    description: selectRole?.description || "",
    permissions,
  };
};

// --------------------------------------------------
// Component
// --------------------------------------------------

const ManageRole: React.FC<ManageRoleProps> = (props) => {
  const { selectRole, onHide, open, title } = props;

  const dispatch = useDispatch();
  const classes = useStyles();

  // IMPORTANT:
  // Check your roleSlice initialState and use its actual
  // module-list property name here.
  const { moduleLists } = useSelector((state: any) => state.role);
  const roleState = useSelector((state: any) => state.role);

  const isAddRoleSuccess = roleState?.isAddRoleSuccess;

  const allModules: Module[] = Array.isArray(moduleLists)
  ? moduleLists
  : [];

  const [roleDetail, setRoleDetail] = useState<RoleDetail>({
    role: "",
    role_id: "",
    description: "",
    permissions: {},
  });

  const [fullWidth] = useState(true);

  const [maxWidth] = useState<"sm" | "xs" | "md" | "lg" | "xl" | false>(
    "md",
  );

  const [isPermissionError, setIsPermissionError] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [checkPermission, setCheckPermission] = useState(false);

  const [alerModeShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);

  // --------------------------------------------------
  // Validation
  // --------------------------------------------------

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    role: roleDetail?.role,
  });

  // --------------------------------------------------
  // Reset role
  // --------------------------------------------------

  const resetForm = useCallback(() => {
    setRoleDetail({
      role: "",
      role_id: "",
      description: "",
      permissions: createRolePermissions(allModules),
    });

    setIsPermissionError(false);
    setIsValid(false);
    setCheckPermission(false);

    resetAllValidator();

    dispatch(
      setNotificationAction({
        message: [],
        severity: "error",
      }),
    );
  }, [allModules, dispatch, resetAllValidator]);

  // --------------------------------------------------
  // Fetch modules
  // --------------------------------------------------

  useEffect(() => {
    if (open) {
      dispatch(getAllModules());
    }
  }, [open, dispatch]);

  // --------------------------------------------------
  // Initialize Add/Edit Role
  // --------------------------------------------------

  useEffect(() => {
    if (!open || !allModules.length) return;

    const updatedRoleDetail = buildRoleDetail(
      allModules,
      selectRole,
    );

    setRoleDetail(updatedRoleDetail);

    setIsPermissionError(false);
    setIsValid(false);
    setCheckPermission(false);
  }, [open, allModules, selectRole]);

  // --------------------------------------------------
  // Handle successful role submission
  // --------------------------------------------------

  useEffect(() => {
    if (isAddRoleSuccess) {
      resetForm();
      dispatch(setAddRoleResponse(false));
      onHide();
    }
  }, [isAddRoleSuccess, resetForm, dispatch, onHide]);

  // --------------------------------------------------
  // Close modal
  // --------------------------------------------------

  const hideHandler = () => {
    resetForm();
    setAlertModelShow(false);
    setIsClickReset(false);
    onHide();
  };

  // --------------------------------------------------
  // Format module/group names
  // --------------------------------------------------

  const formatPermissionKey = (key: string) => {
    return key
      .replace(/_/g, " ")
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(" ");
  };

  // --------------------------------------------------
  // Dynamic permission grouping
  // --------------------------------------------------

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, string[]> = {};

    Object.keys(roleDetail.permissions).forEach((moduleName) => {
      const groupName = moduleName.split(":")[0].trim();

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(moduleName);
    });

    return groups;
  }, [roleDetail.permissions]);

  // --------------------------------------------------
  // Group checkbox status
  // --------------------------------------------------

  const getGroupPermissionStatus = (
    group: string,
    permissionType: PermissionType,
  ) => {
    const modules = groupedPermissions[group] || [];

    const checkedCount = modules.filter(
      (moduleName) =>
        roleDetail.permissions[moduleName]?.[permissionType],
    ).length;

    return {
      checked:
        modules.length > 0 && checkedCount === modules.length,

      indeterminate:
        checkedCount > 0 && checkedCount < modules.length,
    };
  };

  // --------------------------------------------------
  // Disable specific Surveyor permissions
  // --------------------------------------------------

  const isPermissionDisabled = (
    moduleName: string,
    permissionType: PermissionType,
  ) => {
    const isSurveyor =
      selectRole?.role?.toLowerCase() === "surveyor";

    if (isSurveyor && moduleName === "Surveyor") {
      return true;
    }

    if (
      isSurveyor &&
      permissionType === "read" &&
      ["Lead", "Lead:Survey", "Surveyor"].some(
        (module) =>
          normalizeModuleName(module) ===
          normalizeModuleName(moduleName),
      )
    ) {
      return true;
    }

    return false;
  };

  // --------------------------------------------------
  // Handle group checkbox
  // --------------------------------------------------

  const handleGroupPermissionChange = (
    group: string,
    permissionType: PermissionType,
    value: boolean,
  ) => {
    setRoleDetail((prevDetail) => {
      const updatedPermissions = {
        ...prevDetail.permissions,
      };

      (groupedPermissions[group] || [])
        .filter(
          (moduleName) =>
            !isPermissionDisabled(moduleName, permissionType),
        )
        .forEach((moduleName) => {
          updatedPermissions[moduleName] = {
            ...updatedPermissions[moduleName],
            [permissionType]: value,
          };
        });

      return {
        ...prevDetail,
        permissions: updatedPermissions,
      };
    });

    setCheckPermission(true);
  };

  // --------------------------------------------------
  // Handle individual checkbox
  // --------------------------------------------------

  const handlePermissionChange = (
    moduleName: string,
    permission: PermissionType,
    value: boolean,
  ) => {
    setRoleDetail((prevDetail) => ({
      ...prevDetail,
      permissions: {
        ...prevDetail.permissions,
        [moduleName]: {
          ...prevDetail.permissions[moduleName],
          [permission]: value,
        },
      },
    }));

    setCheckPermission(true);
  };

  // --------------------------------------------------
  // Check selected permissions
  // --------------------------------------------------

  const isAnyPermissionSelected = (
    permissions: Record<string, ModulePermission>,
  ) => {
    return Object.values(permissions).some((permission) =>
      permissionTypes.some((type) => permission[type]),
    );
  };

  useEffect(() => {
    if (!checkPermission) return;

    const hasPermission = isAnyPermissionSelected(
      roleDetail.permissions,
    );

    setIsValid(hasPermission);
    setIsPermissionError(!hasPermission);
    setCheckPermission(false);
  }, [checkPermission, roleDetail.permissions]);

  // --------------------------------------------------
  // Build API payload
  // --------------------------------------------------

  const buildPayload = () => {
    const modules = Object.keys(roleDetail.permissions)
      .map((moduleName) => {
        const permissions = permissionTypes.filter(
          (permission) =>
            roleDetail.permissions[moduleName][permission],
        );

        // Find original API module name.
        const apiModule = allModules.find(
          (item) =>
            normalizeModuleName(item.module_name) ===
            normalizeModuleName(moduleName),
        );

        return {
          module_name:
            apiModule?.module_name || moduleName,
          permissions,
        };
      })
      .filter((module) => module.permissions.length > 0);

    return {
      role: roleDetail.role,
      role_id: roleDetail.role_id,
      description: roleDetail.description,
      modules,
    };
  };

  // --------------------------------------------------
  // Submit
  // --------------------------------------------------

  const handleSubmit = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();

    checkValidator();

    const hasPermission = isAnyPermissionSelected(
      roleDetail.permissions,
    );

    setIsValid(hasPermission);
    setIsPermissionError(!hasPermission);

    if (!hasPermission) {
      return;
    }

    if (!isError() && error !== null) {
      const payload = buildPayload();

      dispatch(addRoleAction(payload));
    }
  };

  // --------------------------------------------------
  // Input changes
  // --------------------------------------------------

  const handleInputChange = (
    field: string,
    value: any,
  ) => {
    setRoleDetail((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    resetValidator(field);
  };

  // --------------------------------------------------
  // Clear/reset confirmation
  // --------------------------------------------------

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  const handleClear = useCallback(() => {
    // Restore original selected role when editing.
    // Otherwise initialize all modules with false permissions.

    const updatedRoleDetail = buildRoleDetail(
      allModules,
      selectRole,
    );

    setRoleDetail(updatedRoleDetail);

    resetAllValidator();

    setIsClickReset(false);
    setAlertModelShow(false);
    setIsPermissionError(false);
    setIsValid(false);
    setCheckPermission(false);

    dispatch(
      setNotificationAction({
        message: [],
        severity: "error",
      }),
    );
  }, [
    allModules,
    selectRole,
    resetAllValidator,
    dispatch,
  ]);

  useEffect(() => {
    if (isClickReset) {
      handleClear();
    }
  }, [isClickReset, handleClear]);

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#fff"
        header={
          <>
            <img
              onClick={handleSubmit}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />

            <img
              onClick={openClearPopup}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="reset"
            />

            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "10px" }}>
          <DialogContentText>
            <Notification />

            {/* Role details */}

            <Grid
              container
              spacing={0.5}
              sx={{
                background: "#E5F4F1",
                border: "1px solid #CFCFCF",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              {roleAllFields.map((field, i) => (
                <FormField
                  key={i}
                  onPaste={(e: React.ClipboardEvent) =>
                    e.preventDefault()
                  }
                  onKeyDown={(event: any) => {
                    if (
                      [
                        8, 9, 17, 18, 37, 38, 39, 40,
                      ].includes(event.keyCode) ||
                      event.ctrlKey
                    ) {
                      return;
                    }

                    if (
                      field?.patternAllowed &&
                      !field.patternAllowed.test(event.key)
                    ) {
                      event.preventDefault();
                    } else if (
                      field?.patternAllowed &&
                      !field.patternAllowed.test(
                        event.target.value + event.key,
                      )
                    ) {
                      event.preventDefault();
                    }
                  }}
                  {...field}
                  disabled={
                    field?.id === "role" &&
                    selectRole?.role === "Surveyor"
                  }
                  // variant="outlined"
                  inSingleGridForm
                  isNonMarginTop
                  InputProps={{
                    ...(field?.inputProps || {}),
                    sx: { height: "36px" },
                    autocomplete: "user-new-field",
                  }}
                  value={roleDetail[field.id] || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange(field.id, e.target.value)
                  }
                  error={error && error[field?.id]}
                />
              ))}
            </Grid>

            {/* Permission heading */}

            <Grid
              container
              spacing={2}
              sx={{ marginTop: "6px" }}
            >
              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  border: "2px solid #dbdbdb",
                  borderTopLeftRadius: "5px",
                  borderBottomLeftRadius: "5px",
                  padding: "8px",
                  background: "#E5F4F1",
                }}
              >
                <Typography
                  sx={{
                    color: "#333333",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Permissions
                  <span
                    style={{
                      color: "#ff0017",
                      fontWeight: 500,
                    }}
                  >
                    *
                  </span>
                </Typography>
              </Grid>

              {/* Permission column headings */}

              <Grid
                item
                xs={12}
                sm={8}
                sx={{
                  background: "#E5F4F1",
                  padding: "6px 8px",
                  border: "1px solid #CFCFCF",
                  borderTopRightRadius: "5px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                <Grid container alignItems="center">
                  {permissionTypes.map((permission) => (
                    <Grid
                      item
                      xs={3}
                      key={permission}
                      sx={{
                        textAlign: "center",
                        fontSize: "12px",
                      }}
                    >
                      {formatPermissionKey(permission)}
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Dynamic permissions */}

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  background: "#fff",
                  paddingBottom: "6px",
                  border:
                    isPermissionError && !isValid
                      ? "1px solid #d32f2f"
                      : "1px solid #dbdbdb",
                  borderTopLeftRadius: "5px",
                  paddingLeft: "0px",
                }}
              >
                {Object.keys(groupedPermissions).map((group) => (
                  <Grid
                    container
                    key={group}
                    sx={{ mb: 1 }}
                  >
                    {/* Group heading */}

                    <Grid
                      container
                      alignItems="center"
                      sx={{
                        background: "#f6f7f8",
                        borderBottom: "1px solid #dbdbdb",
                        padding: "8px 0",
                        minHeight: "36px",
                        paddingLeft: "8px",
                      }}
                    >
                      <Grid item xs={4}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "14px",
                          }}
                        >
                          {formatPermissionKey(group)} Group
                        </Typography>
                      </Grid>

                      {/* Group-level checkboxes */}

                      {permissionTypes.map((permissionType) => {
                        const status = getGroupPermissionStatus(
                          group,
                          permissionType,
                        );

                        const groupModules =
                          groupedPermissions[group] || [];

                        return (
                          <Grid
                            item
                            xs={2}
                            key={permissionType}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              size="small"
                              sx={{ padding: "0px" }}
                              checked={status.checked}
                              indeterminate={status.indeterminate}
                              disabled={groupModules.every(
                                (moduleName) =>
                                  isPermissionDisabled(
                                    moduleName,
                                    permissionType,
                                  ),
                              )}
                              onChange={(e) =>
                                handleGroupPermissionChange(
                                  group,
                                  permissionType,
                                  e.target.checked,
                                )
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Individual module rows */}

                    {(groupedPermissions[group] || []).map(
                      (moduleName) => (
                        <Grid
                          container
                          key={moduleName}
                          alignItems="center"
                          sx={{
                            borderBottom: "1px solid #f0f0f0",
                            padding: "8px 0",
                          }}
                        >
                          <Grid
                            item
                            xs={4}
                            sx={{
                              fontSize: "14px",
                              paddingLeft: "8px",
                            }}
                          >
                            {formatPermissionKey(moduleName)}
                          </Grid>

                          {permissionTypes.map((permissionType) => (
                            <Grid
                              item
                              xs={2}
                              key={permissionType}
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                paddingLeft: "6px",
                              }}
                            >
                              <Checkbox
                                size="small"
                                sx={{ padding: "0px" }}
                                disabled={isPermissionDisabled(
                                  moduleName,
                                  permissionType,
                                )}
                                checked={
                                  !!roleDetail.permissions[moduleName]?.[
                                    permissionType
                                  ]
                                }
                                onChange={(e) =>
                                  handlePermissionChange(
                                    moduleName,
                                    permissionType,
                                    e.target.checked,
                                  )
                                }
                                name={`${moduleName}-${permissionType}`}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ),
                    )}
                  </Grid>
                ))}

                {!allModules.length && (
                  <Typography
                    sx={{
                      padding: 2,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    No modules available.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Popup>

      {/* Reset confirmation */}

      {alerModeShow && (
        <ResetFormAlert
          open={alerModeShow}
          title=""
          alertModalShow={alerModeShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageRole;
